/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Grid, PageGridContainer, Typography, breakpoints } from '@coursera/cds-core';
import type { GridProps } from '@coursera/cds-core';

type PartnerLogo = {
  id?: string | null;
  url?: string | null;
  altText?: string | null;
};

type Props = {
  title?: string | null;
  subtitle?: string | null;
  partnerLogos?: Array<PartnerLogo> | null;
  valuePropCards?: Array<React.ReactNode>;
  valuePropBanner?: React.ReactNode;
  showPartnerLogos?: boolean;
};

const SALESFORCE_IMAGE_ASSET_ID = '6qmeI8OvobY6YjTBtbCTWh';
const INTUIT_IMAGE_ASSET_ID = '2MUk5OFVjEGjSfr7ZI6lFL';
const META_IMAGE_ASSET_ID = '6wnZdnONeaKCZfaYCEVYE7';
const IBM_IMAGE_ASSET_ID = '1fR1vY63NndaNBXIxbRNZn';

// TODO (Connor): We should add a 'height' property in contentful image instead of hardcoding.
const OVERRIDE_LOGO_STYLING_MAPPING = {
  [SALESFORCE_IMAGE_ASSET_ID]: 'salesforce-logo',
  [INTUIT_IMAGE_ASSET_ID]: 'intuit-logo',
  [META_IMAGE_ASSET_ID]: 'meta-logo',
  [IBM_IMAGE_ASSET_ID]: 'ibm-logo',
};

const styles = {
  section: css`
    background-color: var(--cds-color-grey-25);
    padding: var(--cds-spacing-600) 0;
  `,
  headingContainer: css`
    text-align: center;
  `,
  headingTitle: css`
    margin-bottom: 2px;
  `,
  partnerLogosContainer: css`
    ${breakpoints.down('sm')} {
      margin: var(--cds-spacing-300) 0;
    }
  `,
  partnerLogoContainer: css`
    margin: 10px 30px;
    max-height: 40px;

    img {
      height: 35px;
    }

    img.ibm-logo {
      height: 24px;
    }

    img.meta-logo {
      height: 38px;
    }

    img.intuit-logo {
      height: 24px;
    }

    img.salesforce-logo {
      height: 45px;
    }
  `,
  cardWrapper: css`
    ${breakpoints.down('sm')} {
      margin-bottom: 24px;
      margin: 0;
    }
  `,
};

// We only support proper sizing for 3 or 4 cards.
const getValuePropCardColumnSize = (cardsLength: number) => {
  switch (cardsLength) {
    case 3:
      return 4;
    case 4:
      return 3;
    default:
      return 12;
  }
};

const getValuePropCardSpacing = (cardsLength: number): GridProps['spacing'] => {
  switch (cardsLength) {
    case 4:
      return 24;
    default:
      return 48;
  }
};

const ValuePropsModule = ({
  title,
  subtitle,
  partnerLogos,
  valuePropCards,
  valuePropBanner,
  showPartnerLogos,
}: Props) => {
  if (!title || !valuePropCards) {
    return null;
  }

  return (
    <section css={styles.section}>
      <PageGridContainer>
        <Grid item container xs={12} justifyContent="center" direction="column" css={styles.headingContainer}>
          <Typography variant="d2semibold" component="h2" css={styles.headingTitle}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="h2" component="p">
              {subtitle}
            </Typography>
          )}
        </Grid>
        <Grid item container xs={12} alignItems="center" justifyContent="center" css={styles.partnerLogosContainer}>
          {!!showPartnerLogos &&
            partnerLogos?.map(({ url, altText, id }) => {
              if (!url) {
                return null;
              } else {
                const assetId = id as keyof typeof OVERRIDE_LOGO_STYLING_MAPPING;

                return (
                  <div css={styles.partnerLogoContainer}>
                    <Imgix
                      src={url}
                      alt={altText || ''}
                      maxHeight={60}
                      maxWidth={155}
                      className={
                        assetId && OVERRIDE_LOGO_STYLING_MAPPING[assetId] ? OVERRIDE_LOGO_STYLING_MAPPING[assetId] : ''
                      }
                    />
                  </div>
                );
              }
            })}
        </Grid>
        <Grid item container justifyContent="center">
          <Grid container xs={12} spacing={getValuePropCardSpacing(valuePropCards.length)} css={styles.cardWrapper}>
            {valuePropCards?.map((cardComponent) => (
              <Grid item xs={12} md={getValuePropCardColumnSize(valuePropCards.length)}>
                {cardComponent}
              </Grid>
            ))}
          </Grid>
          {valuePropBanner && (
            <Grid item xs={12}>
              {valuePropBanner}
            </Grid>
          )}
        </Grid>
      </PageGridContainer>
    </section>
  );
};

export default ValuePropsModule;
